import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { EaContext, eaParams, useEaFilterState } from "@/components/energy-analytics/analytics/EaContext";
import { EaSidebar } from "@/components/energy-analytics/analytics/EaSidebar";
import { EaEmptyState } from "@/components/energy-analytics/analytics/EaUi";

const COLLAPSE_KEY = "ea_sidebar_collapsed";

function CompanyPicker({ value, onChange }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/ea/companies").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="ea-company-select"
      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm max-w-[260px]">
      <option value="">Vedi come cliente…</option>
      {list.map((c) => <option key={c.id} value={c.id}>{c.name}{c.has_data ? "" : " (senza dati)"}</option>)}
    </select>
  );
}

function YearPicker({ years, value, onChange }) {
  if (!years?.length) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="ea-year-select"
      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold">
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

export function EaRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/energy-analytics/overview", search }} replace />;
}

export default function EaLayout() {
  const { user } = useAuth();
  const loc = useLocation();
  const isStaff = user?.role === "admin" || user?.role === "editor";
  const fs = useEaFilterState();
  const { filters, setFilter, search } = fs;
  const [nav, setNav] = useState(null);
  const [navError, setNavError] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const needsCompany = isStaff && !filters.company;

  useEffect(() => setMobileOpen(false), [loc.pathname]);

  useEffect(() => {
    if (needsCompany) { setNav(null); return; }
    let alive = true;
    setNavError(null);
    api.get("/ea/navigation", { params: eaParams(filters) })
      .then(({ data }) => {
        if (!alive) return;
        setNav(data);
        if (data.year && String(data.year) !== filters.year) setFilter({ year: data.year });
      })
      .catch((e) => { if (!alive) return; const d = e?.response?.data?.detail; setNav(null); setNavError(typeof d === "string" ? d : d?.message || "Errore di caricamento"); });
    return () => { alive = false; };
  }, [filters.company, filters.year, needsCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); };
  const groups = nav?.groups || [];
  const ready = !!nav && String(nav.year || "") === (filters.year || "");
  const ctx = { ...fs, nav, ready, isStaff };

  let body = <Outlet />;
  if (needsCompany) body = <EaEmptyState testid="ea-select-company" title="Seleziona un cliente" text="Scegli in alto il cliente da visualizzare come lo vedrebbe lui. Sono elencati solo i clienti con Energy Analytics attivo." />;
  else if (navError) body = <EaEmptyState testid="ea-access-error" title="Energy Analytics non disponibile" text={navError} />;
  else if (nav && !nav.years.length) body = <EaEmptyState testid="ea-no-data" title="Nessun dato disponibile" text="Non sono ancora stati pubblicati dati di consumo e costo per questo cliente." />;
  else if (!ready) body = <div className="text-slate-400 text-sm" data-testid="ea-loading">Caricamento…</div>;

  return (
    <EaContext.Provider value={ctx}>
      <div className="min-h-screen bg-[#F6F8FC] flex" data-testid="ea-layout">
        <aside className={`hidden lg:flex flex-col bg-[#071B4D] text-white fixed inset-y-0 left-0 z-30 transition-[width] duration-200 ${collapsed ? "w-[76px]" : "w-64"}`} data-testid="ea-sidebar">
          <EaSidebar groups={groups} search={search} collapsed={collapsed} onToggle={toggle} />
        </aside>
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#071B4D] text-white flex flex-col" data-testid="ea-sidebar-mobile">
              <EaSidebar groups={groups} search={search} collapsed={false} onClose={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}
        <div className={`flex-1 flex flex-col min-w-0 transition-[padding] duration-200 ${collapsed ? "lg:pl-[76px]" : "lg:pl-64"}`}>
          <header className="sticky top-0 z-20 min-h-16 bg-[#F6F8FC]/85 backdrop-blur-lg border-b border-slate-200 flex items-center justify-between gap-3 px-4 lg:px-8 py-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden p-2 rounded-lg hover:bg-slate-200" onClick={() => setMobileOpen(true)} data-testid="ea-mobile-menu-btn"><Menu size={22} /></button>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Energy Analytics</div>
                <div className="font-display font-semibold text-slate-900 truncate" data-testid="ea-company-name">{nav?.company?.name || (isStaff ? "Anteprima staff" : user?.company_name)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isStaff && <CompanyPicker value={filters.company} onChange={(v) => setFilter({ company: v, year: "", day: "", month: "" })} />}
              <YearPicker years={nav?.years} value={filters.year} onChange={(v) => setFilter({ year: v, day: "", month: "" })} />
            </div>
          </header>
          <main className="flex-1 p-4 lg:p-8" id="ea-page-root">
            <div className="max-w-[1600px] mx-auto fade-up">{body}</div>
          </main>
        </div>
      </div>
    </EaContext.Provider>
  );
}
